/**
 * Trendy Home — theme behaviour.
 * Header state, drawers, predictive search, cart mutations, variant selection.
 */

const strings = window.themeStrings || {};

const money = (cents) =>
  new Intl.NumberFormat('ar-EG', {
    style: 'currency',
    currency: window.Shopify?.currency?.active || 'EGP',
    maximumFractionDigits: 2,
  }).format(cents / 100);

/* ------------------------------------------------------------------ header */

function initHeader() {
  const header = document.querySelector('[data-header]');
  if (!header) return;

  const overlay = header.dataset.overlay === 'true';
  const autohide = header.dataset.autohide === 'true';
  let lastY = window.scrollY;

  const sync = () => {
    const y = window.scrollY;

    /* Transparent only while the hero is still under it, and only where there is a hero. */
    if (overlay) header.dataset.transparent = String(y <= 8);

    /*
     * Opt-in only. When on, it hides on the way down and comes straight back on the way up:
     * the 6px threshold keeps the trackpad's jitter from flapping it, and it never hides in
     * the top strip. Off by default — a header that slides away mid-scroll reads as broken.
     */
    if (!autohide) return;

    const delta = y - lastY;

    if (Math.abs(delta) > 6) {
      header.dataset.hidden = String(delta > 0 && y > 140);
      lastY = y;
    }
  };

  window.addEventListener('scroll', sync, { passive: true });
  sync();
}

/* ----------------------------------------------------------------- drawers */

const openDrawers = new Set();

function setDrawer(id, open) {
  const drawer = document.getElementById(id);
  if (!drawer) return;

  drawer.dataset.open = String(open);
  drawer.setAttribute('aria-hidden', String(!open));

  if (open) openDrawers.add(id);
  else openDrawers.delete(id);

  document.documentElement.style.overflow = openDrawers.size ? 'hidden' : '';

  document
    .querySelectorAll(`[data-drawer-open="${id}"]`)
    .forEach((trigger) => trigger.setAttribute('aria-expanded', String(open)));

  if (open) drawer.querySelector('[data-drawer-close]')?.focus();
}

function initDrawers() {
  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-drawer-open]');
    if (opener) {
      event.preventDefault();
      setDrawer(opener.dataset.drawerOpen, true);
      return;
    }

    const closer = event.target.closest('[data-drawer-close]');
    if (closer) {
      const drawer = closer.closest('[data-drawer]');
      if (drawer) setDrawer(drawer.id, false);
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    [...openDrawers].forEach((id) => setDrawer(id, false));
  });
}

/* -------------------------------------------------------------- accordions */

function initAccordions() {
  document.addEventListener('click', (event) => {
    const toggle = event.target.closest('[data-accordion-toggle]');
    if (!toggle) return;

    const panel = toggle.nextElementSibling;
    const open = toggle.getAttribute('aria-expanded') === 'true';
    toggle.setAttribute('aria-expanded', String(!open));
    if (panel) panel.hidden = open;
  });
}

/* --------------------------------------------------------- header submenus */

function initSubmenus() {
  const toggles = document.querySelectorAll('[data-submenu-toggle]');
  if (!toggles.length) return;

  const closeAll = (except) => {
    toggles.forEach((toggle) => {
      if (toggle !== except) toggle.setAttribute('aria-expanded', 'false');
    });
  };

  toggles.forEach((toggle) => {
    toggle.addEventListener('click', (event) => {
      event.preventDefault();
      const open = toggle.getAttribute('aria-expanded') === 'true';
      closeAll(toggle);
      toggle.setAttribute('aria-expanded', String(!open));
    });
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') closeAll();
  });

  document.addEventListener('click', (event) => {
    if (!event.target.closest('[data-submenu-toggle], .hdr__submenu')) closeAll();
  });
}

/* ------------------------------------------------------ predictive search */

function initSearch() {
  const dialog = document.getElementById('search-dialog');
  if (!dialog) return;

  const input = dialog.querySelector('[data-search-input]');
  const results = dialog.querySelector('[data-search-results]');

  let controller = null;
  let debounce = null;

  const render = (products, term) => {
    if (!products.length) {
      results.innerHTML = `<p class="search-dialog__empty">${strings.noResults || ''}</p>`;
      return;
    }

    const items = products
      .map((product) => {
        const image = product.featured_image?.url
          ? `<img class="sresult__img" src="${product.featured_image.url}" alt="" loading="lazy" width="54" height="54">`
          : '<span class="sresult__img"></span>';

        return `
          <a class="sresult" href="${product.url}">
            ${image}
            <span class="sresult__body">
              <span class="sresult__title">${product.title}</span>
              <span class="sresult__price">${money(product.price)}</span>
            </span>
          </a>`;
      })
      .join('');

    const viewAll = `<a class="search-dialog__foot" href="/search?q=${encodeURIComponent(term)}">${
      strings.viewAllResults || ''
    }</a>`;

    results.innerHTML = items + viewAll;
  };

  const search = async (term) => {
    /* Abort the in-flight request so a slow one can't land on top of a newer result. */
    controller?.abort();
    controller = new AbortController();

    const params = new URLSearchParams({
      q: term,
      'resources[type]': 'product',
      'resources[limit]': '6',
    });

    try {
      const response = await fetch(`/search/suggest.json?${params}`, { signal: controller.signal });
      const data = await response.json();
      render(data.resources?.results?.products ?? [], term);
    } catch (error) {
      if (error.name !== 'AbortError') results.innerHTML = '';
    }
  };

  input.addEventListener('input', () => {
    const term = input.value.trim();
    clearTimeout(debounce);

    if (term.length < 2) {
      controller?.abort();
      results.innerHTML = '';
      return;
    }

    debounce = setTimeout(() => search(term), 220);
  });

  document.addEventListener('click', (event) => {
    if (event.target.closest('[data-search-open]')) {
      dialog.showModal();
      input.focus();
    }

    if (event.target.closest('[data-search-close]')) dialog.close();
  });

  /* A click that lands on the dialog element itself is a click on its backdrop. */
  dialog.addEventListener('click', (event) => {
    if (event.target === dialog) dialog.close();
  });
}

/* -------------------------------------------------------------------- cart */

/**
 * Swaps in freshly rendered cart-drawer markup returned by the Section Rendering API,
 * so the drawer can never drift out of sync with the real cart.
 */
function applyCartSection(html, itemCount) {
  document.querySelectorAll('[data-cart-count]').forEach((el) => {
    el.textContent = itemCount;
    el.hidden = itemCount === 0;
  });

  if (!html) return;

  const parsed = new DOMParser().parseFromString(html, 'text/html');

  const body = document.querySelector('[data-cart-body]');
  const freshBody = parsed.querySelector('[data-cart-body]');
  if (body && freshBody) body.innerHTML = freshBody.innerHTML;

  const foot = document.querySelector('[data-cart-foot]');
  const freshFoot = parsed.querySelector('[data-cart-foot]');
  if (foot && freshFoot) {
    foot.innerHTML = freshFoot.innerHTML;
    foot.hidden = freshFoot.hasAttribute('hidden');
  }
}

async function mutateCart(url, payload) {
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ ...payload, sections: 'cart-drawer' }),
  });

  if (!response.ok) throw new Error(`Cart request failed: ${response.status}`);
  return response.json();
}

function initCart() {
  const onCartPage = document.body.classList.contains('template-cart');

  /* Quantity stepper on the product page — local only, nothing is in the cart yet. */
  document.addEventListener('click', (event) => {
    const step = event.target.closest('[data-qty-step]');
    if (!step) return;

    const input = step.parentElement.querySelector('.qty__input');
    if (!input) return;

    const next = Number(input.value) + Number(step.dataset.qtyStep);
    input.value = Math.max(1, next);
  });

  /* Quantity steppers and remove buttons — used by both the drawer and the cart page. */
  document.addEventListener('click', async (event) => {
    const button = event.target.closest('[data-qty-change]');
    if (!button) return;

    event.preventDefault();
    button.disabled = true;

    try {
      const cart = await mutateCart('/cart/change.js', {
        id: button.dataset.key,
        quantity: Number(button.dataset.qtyChange),
      });

      /* The cart page has totals and line rows the drawer markup doesn't cover — reload it. */
      if (onCartPage) window.location.reload();
      else applyCartSection(cart.sections?.['cart-drawer'], cart.item_count);
    } catch (error) {
      console.error(error);
      button.disabled = false;
    }
  });

  /* Add to cart. */
  document.addEventListener('submit', async (event) => {
    const form = event.target.closest('[data-product-form]');
    if (!form) return;

    event.preventDefault();

    const button = form.querySelector('[data-add-button]');
    const label = button.querySelector('[data-add-label]');
    const original = label.textContent;

    button.disabled = true;
    label.textContent = strings.adding || '';

    try {
      const cart = await mutateCart('/cart/add.js', {
        items: [
          {
            id: Number(form.querySelector('[name="id"]').value),
            quantity: Number(form.querySelector('[name="quantity"]')?.value || 1),
          },
        ],
      });

      applyCartSection(cart.sections?.['cart-drawer'], cart.item_count);

      label.textContent = strings.added || '';
      setTimeout(() => {
        label.textContent = original;
        button.disabled = false;
      }, 1400);

      if (document.getElementById('cart-drawer')) setDrawer('cart-drawer', true);
      else window.location.href = '/cart';
    } catch (error) {
      console.error(error);
      label.textContent = strings.errorAdding || original;
      button.disabled = false;
    }
  });
}

/* --------------------------------------------------------------- cart note */

/**
 * The note lives inside the cart page's form, so it is only persisted when that form is
 * submitted. Anyone who types a note and then checks out from the cart popup — which links
 * straight to /checkout — would lose it. Saving on a pause covers every route out.
 */
function initCartNote() {
  const note = document.getElementById('cart-note');
  if (!note) return;

  let debounce = null;

  note.addEventListener('input', () => {
    clearTimeout(debounce);

    debounce = setTimeout(async () => {
      try {
        await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ note: note.value }),
        });
      } catch (error) {
        console.error(error);
      }
    }, 600);
  });
}

/* ---------------------------------------------------------- footer columns */

/**
 * The footer columns are <details>. Below 750px they behave as dropdowns; above it they are
 * pinned open so they read as plain columns. Forcing them open in CSS is not reliable —
 * browsers hide a closed <details>' content through the shadow tree, not through the child's
 * display — so the open attribute is set here instead.
 */
function initFooterCols() {
  const cols = document.querySelectorAll('.ftr__col');
  if (!cols.length) return;

  const desktop = window.matchMedia('(min-width: 750px)');

  const sync = () => {
    cols.forEach((col) => {
      col.open = desktop.matches;
    });
  };

  desktop.addEventListener('change', sync);
  sync();
}

/* ------------------------------------------------------------------- rails */

function initRails() {
  document.querySelectorAll('[data-rail]').forEach((rail) => {
    /*
     * [data-rail-scope] narrows the button lookup. Sections with one rail need nothing; the
     * category tabs hold four, and without a scope every rail would bind to the first pair.
     */
    const section =
      rail.closest('[data-rail-scope]') || rail.closest('.shopify-section') || rail.parentElement;
    const prev = section.querySelector('[data-rail-prev]');
    const next = section.querySelector('[data-rail-next]');

    /* One card plus its gap. Reading it off the DOM keeps this in step with the CSS. */
    const step = () => {
      const card = rail.firstElementChild;
      if (!card) return rail.clientWidth;
      const gap = parseFloat(getComputedStyle(rail).columnGap) || 0;
      return card.getBoundingClientRect().width + gap;
    };

    /*
     * scrollLeft runs negative in RTL, so "forward" is a different sign in each direction.
     * Reading the computed direction is safer than assuming the document's.
     */
    const forward = () => (getComputedStyle(rail).direction === 'rtl' ? -1 : 1);

    const sync = () => {
      if (!prev || !next) return;
      const max = rail.scrollWidth - rail.clientWidth;
      const travelled = Math.abs(rail.scrollLeft);
      prev.disabled = travelled < 4;
      next.disabled = travelled >= max - 4;
    };

    prev?.addEventListener('click', () => rail.scrollBy({ left: -step() * forward() }));
    next?.addEventListener('click', () => rail.scrollBy({ left: step() * forward() }));

    rail.addEventListener('scroll', sync, { passive: true });
    window.addEventListener('resize', sync);
    sync();

    /* Click-and-drag, so a mouse can pan the rail without reaching for shift+wheel. */
    let down = false;
    let startX = 0;
    let startScroll = 0;

    rail.addEventListener('pointerdown', (event) => {
      if (event.pointerType !== 'mouse') return;
      down = true;
      startX = event.clientX;
      startScroll = rail.scrollLeft;
      rail.style.scrollSnapType = 'none';
      rail.style.scrollBehavior = 'auto';
      rail.style.cursor = 'grabbing';
    });

    rail.addEventListener('pointermove', (event) => {
      if (!down) return;
      event.preventDefault();
      rail.scrollLeft = startScroll - (event.clientX - startX);
    });

    const release = () => {
      if (!down) return;
      down = false;
      rail.style.scrollSnapType = '';
      rail.style.scrollBehavior = '';
      rail.style.cursor = '';
    };

    rail.addEventListener('pointerup', release);
    rail.addEventListener('pointerleave', release);
  });
}

/* ------------------------------------------------------- collection facets */

function initFacets() {
  const form = document.querySelector('[data-facet-form]');
  if (!form) return;

  /*
   * Empty fields are stripped before submitting. Left in, they add `filter.v.price.gte=`
   * style noise to the URL, and Shopify reads a blank price bound as zero.
   */
  const submit = () => {
    form.querySelectorAll('input[type="number"]').forEach((input) => {
      if (input.value === '') input.disabled = true;
    });
    form.submit();
  };

  form.addEventListener('change', (event) => {
    if (event.target.matches('input[type="checkbox"], select')) submit();
  });

  /* Price is typed, not clicked — wait for a pause rather than firing on every keystroke. */
  let debounce = null;
  form.addEventListener('input', (event) => {
    if (!event.target.matches('input[type="number"]')) return;
    clearTimeout(debounce);
    debounce = setTimeout(submit, 700);
  });

  /* Close an open facet dropdown when the click lands outside it. */
  document.addEventListener('click', (event) => {
    form.querySelectorAll('details[open]').forEach((details) => {
      if (!details.contains(event.target)) details.open = false;
    });
  });
}

/* ---------------------------------------------------------------- wishlist */

/*
 * Shopify has no native wishlist, so this one is a list of product handles in localStorage.
 * No account needed and nothing to install, but it stays on this browser — it does not
 * follow the customer to another device. Swap readWishlist/writeWishlist for an API call
 * against customer metafields if that ever has to change; nothing else here would move.
 */

const WISHLIST_KEY = 'homerise:wishlist';

function readWishlist() {
  try {
    const raw = JSON.parse(localStorage.getItem(WISHLIST_KEY));
    return Array.isArray(raw) ? raw.filter((handle) => typeof handle === 'string') : [];
  } catch (error) {
    return [];
  }
}

function writeWishlist(list) {
  try {
    localStorage.setItem(WISHLIST_KEY, JSON.stringify(list));
  } catch (error) {
    /* Private browsing, or the quota is full. The page still behaves for this visit. */
    console.warn(error);
  }
}

/* Every heart on the page, plus the header counter, re-read from storage. */
function syncWishlist() {
  const list = readWishlist();

  document.querySelectorAll('[data-wishlist-toggle]').forEach((button) => {
    const saved = list.includes(button.dataset.wishlistHandle);
    const label = saved ? strings.wishlistRemove : strings.wishlistAdd;

    button.setAttribute('aria-pressed', String(saved));

    if (label) {
      button.setAttribute('aria-label', label);
      button.title = label;
    }
  });

  document.querySelectorAll('[data-wishlist-count]').forEach((el) => {
    el.textContent = list.length;
    el.hidden = list.length === 0;
  });
}

function renderWishlistState() {
  const grid = document.querySelector('[data-wishlist-grid]');
  if (!grid) return;

  const filled = grid.children.length > 0;

  grid.hidden = !filled;

  const empty = document.querySelector('[data-wishlist-empty]');
  if (empty) empty.hidden = filled;

  const loading = document.querySelector('[data-wishlist-loading]');
  if (loading) loading.hidden = true;
}

function initWishlist() {
  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-wishlist-toggle]');
    if (!button) return;

    event.preventDefault();

    const handle = button.dataset.wishlistHandle;
    if (!handle) return;

    const list = readWishlist();
    const at = list.indexOf(handle);
    const removing = at !== -1;

    if (removing) list.splice(at, 1);
    else list.unshift(handle);

    writeWishlist(list);
    syncWishlist();

    /* Un-hearting something on the wishlist page should take the card with it. */
    if (removing) {
      const item = button.closest('[data-wishlist-item]');
      if (item) {
        item.remove();
        renderWishlistState();
      }
    }
  });

  /* The same list open in another tab. */
  window.addEventListener('storage', (event) => {
    if (event.key === WISHLIST_KEY) syncWishlist();
  });

  syncWishlist();
}

/*
 * The wishlist page holds handles, not products. Each saved handle is rendered through the
 * Section Rendering API against sections/wishlist-card.liquid, which contains nothing but a
 * product card — so these cards can never drift from the ones in any other grid.
 */
async function initWishlistPage() {
  const grid = document.querySelector('[data-wishlist-grid]');
  if (!grid) return;

  const list = readWishlist();

  if (list.length === 0) {
    renderWishlistState();
    return;
  }

  const root = window.Shopify?.routes?.root || '/';

  const results = await Promise.all(
    list.map(async (handle) => {
      try {
        const response = await fetch(`${root}products/${handle}?section_id=wishlist-card`);
        if (!response.ok) return { handle, markup: null };

        const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
        const card = parsed.querySelector('.card');

        return { handle, markup: card ? card.outerHTML : null };
      } catch (error) {
        console.error(error);
        return { handle, markup: null };
      }
    })
  );

  const alive = [];

  results.forEach(({ handle, markup }) => {
    if (!markup) return;

    const item = document.createElement('li');
    item.dataset.wishlistItem = '';
    item.innerHTML = markup;

    grid.append(item);
    alive.push(handle);
  });

  /* Anything that 404s was deleted or unpublished — stop carrying it around. */
  if (alive.length !== list.length) writeWishlist(alive);

  syncWishlist();
  renderWishlistState();
}

/* -------------------------------------------------------------------- boot */

initHeader();
initDrawers();
initAccordions();
initSubmenus();
initSearch();
initCart();
initCartNote();
initFooterCols();
initRails();
initFacets();
initWishlist();
initWishlistPage();
